import { useEffect, useState } from "react";
import api from "../api/api";
import { FaTrash, FaPlus, FaUsers } from "react-icons/fa";

export default function AdminPanel() {
  // States for users
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userError, setUserError] = useState("");

  // States for question form
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [difficulty, setDifficulty] = useState("");
  const [topic, setTopic] = useState("");
  const [questionError, setQuestionError] = useState("");
  const [questionSuccess, setQuestionSuccess] = useState("");
  const [questionLoading, setQuestionLoading] = useState(false);

  // Fetch users on mount
  useEffect(() => {
    const fetchUsers = async () => {
      setLoadingUsers(true);
      setUserError("");
      try {
        const res = await api.get("/admin/users");
        setUsers(res.data.users);
      } catch (err) {
        setUserError("❌ Failed to fetch users");
        console.error(err);
      }
      setLoadingUsers(false);
    };
    fetchUsers();
  }, []);

  // Delete user handler
  const handleDelete = async (userId) => {
    if (!window.confirm("⚠️ Are you sure you want to delete this user?")) return;
    try {
      await api.delete(`/admin/user/${userId}`);
      setUsers(users.filter((u) => u._id !== userId));
    } catch (err) {
      alert("❌ Failed to delete user");
      console.error(err);
    }
  };

  // Add question handler
  const handleAddQuestion = async (e) => {
    e.preventDefault();
    setQuestionError("");
    setQuestionSuccess("");

    if (!title || !difficulty || !topic) {
      setQuestionError("⚠️ Please fill all required fields.");
      return;
    }

    setQuestionLoading(true);
    try {
      await api.post("/admin/add-question", {
        title,
        description,
        difficulty,
        topic,
      });
      setQuestionSuccess("✅ Question added successfully!");
      setTitle("");
      setDescription("");
      setDifficulty("");
      setTopic("");
    } catch (err) {
      setQuestionError(err.response?.data?.message || "❌ Failed to add question");
      console.error(err);
    }
    setQuestionLoading(false);
  };

  return (
    <div className="max-w-7xl mx-auto p-8 bg-gradient-to-br from-white/10 via-purple-100/10 to-indigo-100/10 backdrop-blur-xl rounded-2xl shadow-xl mt-10 border border-white/30">
      <h1
        className="text-4xl font-extrabold mb-8 text-center drop-shadow-sm"
        style={{ color: "#DC143C" }}
      >
        🚀 Admin Dashboard
      </h1>

      {/* Add Question Card */}
      <section className="bg-white/20 backdrop-blur-md shadow-lg rounded-xl p-6 mb-12 border border-white/30">
        <h2 className="text-2xl font-semibold mb-5 flex items-center gap-2 text-white drop-shadow">
          <FaPlus className="text-green-400" /> Add New DSA Question
        </h2>

        {questionError && <p className="mb-3 text-red-400 font-medium">{questionError}</p>}
        {questionSuccess && <p className="mb-3 text-green-400 font-medium">{questionSuccess}</p>}

        <form onSubmit={handleAddQuestion} className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <label className="block font-medium mb-1 text-white">Title *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full border border-white/40 bg-white/10 rounded-md px-4 py-2 text-white placeholder-gray-300 focus:ring-2 focus:ring-purple-400"
              placeholder="Enter question title"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block font-medium mb-1 text-white">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              className="w-full border border-white/40 bg-white/10 rounded-md px-4 py-2 text-white placeholder-gray-300 focus:ring-2 focus:ring-purple-400"
              placeholder="Optional detailed description"
            />
          </div>

          <div>
            <label className="block font-medium mb-1 text-white">Difficulty *</label>
            <select
              value={difficulty}
              onChange={(e) => setDifficulty(e.target.value)}
              className="w-full border border-white/40 bg-white/10 rounded-md px-4 py-2 text-white focus:ring-2 focus:ring-purple-400"
            >
              <option value="">Select difficulty</option>
              <option value="easy">Easy</option>
              <option value="medium">Medium</option>
              <option value="hard">Hard</option>
            </select>
          </div>

          <div>
            <label className="block font-medium mb-1 text-white">Topic *</label>
            <input
              type="text"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              className="w-full border border-white/40 bg-white/10 rounded-md px-4 py-2 text-white placeholder-gray-300 focus:ring-2 focus:ring-purple-400"
              placeholder="e.g. Arrays, Trees, Graphs"
            />
          </div>

          <div className="md:col-span-2 flex justify-end">
            <button
              type="submit"
              disabled={questionLoading}
              className={`px-6 py-2 rounded-lg font-semibold text-white bg-gradient-to-r from-pink-500 to-purple-600 hover:opacity-90 shadow-md transition ${questionLoading ? "opacity-50 cursor-not-allowed" : ""
                }`}
            >
              {questionLoading ? "Adding..." : "Add Question"}
            </button>
          </div>
        </form>
      </section>

      {/* Users Table */}
      <section className="bg-white/20 backdrop-blur-md shadow-lg rounded-xl p-6 border border-white/30">
        <h2 className="text-2xl font-semibold mb-5 flex items-center gap-2 text-white drop-shadow">
          <FaUsers className="text-indigo-400" /> Registered Users
        </h2>

        {loadingUsers && <p className="text-gray-300">Loading users...</p>}
        {userError && <p className="text-red-400 mb-4">{userError}</p>}

        {!loadingUsers && users.length === 0 && (
          <p className="text-gray-300">No users found.</p>
        )}

        {users.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full border border-white/30 rounded-lg shadow-lg">
              <thead className="bg-gradient-to-r from-purple-500/40 to-indigo-500/40 text-white">
                <tr>
                  <th className="px-6 py-3 text-left font-semibold">Name</th>
                  <th className="px-6 py-3 text-left font-semibold">Email</th>
                  <th className="px-6 py-3 text-left font-semibold">Role</th>
                  <th className="px-6 py-3 text-center font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user._id} className="hover:bg-white/10 transition">
                    <td className="px-6 py-4 text-white">{user.name}</td>
                    <td className="px-6 py-4 text-white">{user.email}</td>
                    <td className="px-6 py-4 capitalize text-white">{user.role}</td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={() => handleDelete(user._id)}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/90 hover:bg-red-600 text-white rounded-md shadow transition"
                      >
                        <FaTrash /> Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>

  );
}
